/**
 * Global React Query error → toast handlers, wired into the QueryClient in
 * `index.tsx` (`MutationCache.onError` / `QueryCache.onError`).
 *
 * Extracted here (rather than inline in the app entry) so the suppression and
 * dedupe logic is unit-testable without importing `index.tsx`'s side effects
 * (root render, offline bootstrap, OTA, …).
 *
 * Both handlers surface the standard translated message via a `mutation-error`
 * custom event; `NotificationContext` listens and calls `toast.error`. Dispatching
 * an event (rather than importing the notification context) avoids a circular
 * dependency. See `docs/platform/error-handling-design.md`.
 */
import {
  getErrorDetail,
  getErrorMessage,
  getGenericErrorMessage,
  hasErrorCode,
  isNetworkError,
  getErrorStatus,
} from './errors';

/**
 * Dispatch a toast for a failed request via the shared custom-event path.
 *
 * `level` defaults to `error`, which is what every caller but one wants. The
 * exception is an OUTCOME code (below): the transport says 4xx, the product says
 * "done, and here is what happened next", and a red toast would contradict the
 * sentence it is carrying.
 */
export function dispatchErrorToast(message: string, level: 'error' | 'info' = 'error'): void {
  window.dispatchEvent(new CustomEvent('mutation-error', { detail: { message, level } }));
}

/**
 * Codes that arrive on the error channel and are NOT failures.
 *
 * A route sometimes has to answer 4xx for a request that did exactly what the user
 * asked — the canonical case being a change intercepted by an approval workflow:
 * `PEOPLE_APPROVAL_REQUIRED` means the approval request row was written and
 * COMMITTED, so the edit is parked for a decision rather than refused. The status
 * has to stay 4xx (a 2xx would let every existing caller treat an unapplied change
 * as applied), which leaves the toast as the only place to tell the truth about it.
 *
 * These are still marked surfaced, so a call site's `catch` stays quiet exactly as
 * it does for a real error — the difference is the colour and the verb, not the
 * plumbing. Their message comes from `errors.json` like any other code.
 */
const OUTCOME_NOT_FAILURE_CODES = ['PEOPLE_APPROVAL_REQUIRED'] as const;

function isOutcomeNotFailure(error: unknown): boolean {
  return OUTCOME_NOT_FAILURE_CODES.some((code) => hasErrorCode(error, code));
}

/**
 * Errors these global handlers have already put in front of the user.
 *
 * This is the seam that makes "one failure, one toast" structural rather than a
 * rule each call site has to remember. §4 of `docs/platform/error-handling-design.md`
 * has always said the GLOBAL handler owns the mutation toast, and the ESLint
 * `no-restricted-syntax` selector enforces that for a literal `toast.error` in a
 * `catch` — but it is blind to the spelling call sites actually use,
 * `useApiError().handleMutationError(error)`, which toasts by default. So the
 * documented contract held for one shape and silently failed for the ~100 sites
 * written in the other, each showing the generic message twice (WI 1b863f97).
 *
 * React Query calls `MutationCache.onError` BEFORE the mutation's own `onError`
 * and before `mutateAsync` rejects, so by the time a call site's catch runs the
 * user has already been told. `useApiError` reads this set and stays quiet.
 *
 * Keyed on object IDENTITY, deliberately: a `WeakSet` cannot suppress a second,
 * genuinely different failure that happens to carry the same status and code
 * (message-keyed dedupe would), it needs no time window, and it retains nothing.
 * A surface that wants to say something more specific opts the global handler
 * out with `meta: { suppressGlobalErrorToast: true }` — nothing is marked, and
 * its own toast is then the only one.
 */
const surfacedErrors = new WeakSet<object>();

/** Record that the user has already been shown a toast for `error`. */
function markErrorSurfaced(error: unknown): void {
  if (typeof error === 'object' && error !== null) surfacedErrors.add(error);
}

/**
 * Has a global handler already toasted this exact error object?
 *
 * Read by `useApiError` so a call site's `catch` does not repeat it. Returns
 * false for a primitive throw and for any error no global handler saw (a bare
 * `apiClient`/`fetch` call, which has no `MutationCache` behind it).
 */
export function wasErrorSurfacedGlobally(error: unknown): boolean {
  return typeof error === 'object' && error !== null && surfacedErrors.has(error);
}

// In a read-only demo, mutating requests are rejected with DEMO_READ_ONLY by the
// backend guard. User-initiated saves SHOULD explain why, but background writes
// (auto-saves, read-receipts, telemetry) must not spam a toast per request — so
// the demo toast is throttled to at most once every few seconds.
let lastDemoReadOnlyToastAt = 0;
const DEMO_READ_ONLY_TOAST_INTERVAL_MS = 4000;

/**
 * Global mutation error handler. Shows a user-friendly, translated toast for
 * every failed React Query mutation.
 *
 * A mutation may opt out by setting `meta: { suppressGlobalErrorToast: true }` —
 * used when a surface shows its own domain-specific error message (e.g. the
 * fulfilment pick/pack/ship dialogs name the product + warehouse on
 * INSUFFICIENT_STOCK) and a second generic toast would be redundant.
 */
export function handleMutationError(
  error: unknown,
  _variables?: unknown,
  _context?: unknown,
  mutation?: { meta?: Record<string, unknown> },
): void {
  if (mutation?.meta?.suppressGlobalErrorToast) return;

  if (hasErrorCode(error, 'DEMO_READ_ONLY')) {
    const now = Date.now();
    if (now - lastDemoReadOnlyToastAt < DEMO_READ_ONLY_TOAST_INTERVAL_MS) return;
    lastDemoReadOnlyToastAt = now;
  }

  markErrorSurfaced(error);
  dispatchErrorToast(getErrorMessage(error), isOutcomeNotFailure(error) ? 'info' : 'error');
}

// Short window in which a repeated identical query error is toasted only once,
// so a burst of queries failing with the same backend error shows one toast.
const DEDUPE_WINDOW_MS = 3000;
const recentQueryErrorAt = new Map<string, number>();

/**
 * Clear the dedupe window. **Tests only** — module state otherwise leaks between
 * cases, and a test that reuses a status+code another case already toasted would
 * be silently suppressed rather than failing for a legible reason.
 */
export function resetQueryErrorDedupe(): void {
  recentQueryErrorAt.clear();
}

// Codes that describe a FORM the caller should correct. On a read there is no
// form — a page that fires a GET with a bad path segment has nothing the user
// could check — so the toast falls back to the standard system-error copy with
// its errorId + timestamp reference (docs/platform/error-handling-design.md).
const FORM_VALIDATION_CODES = new Set(['VALIDATION_ERROR']);

/**
 * What makes two query failures "the same failure" for dedupe purposes.
 *
 * NOT the `error_id`: the backend stamps a fresh one per request, so keying on
 * it meant a page whose three reads all failed the same way showed three
 * identical toasts — the dedupe never fired for the case it exists for. Status +
 * code is the coarsest key that still separates genuinely different failures.
 *
 * The cost is deliberate and app-wide: two UNRELATED reads failing with the same
 * status+code inside the window now show one toast. They carried the same
 * sentence either way — only the `error_id` differed, and the axios interceptor
 * logs EVERY failure's `error_id` to the console, which is where support reads
 * it. Adding the query key to this key would separate them again and restore the
 * original bug, so don't. See docs/platform/error-handling-design.md § 4.2.
 */
function queryErrorDedupeKey(error: unknown, message: string): string {
  const code = getErrorDetail(error)?.code;
  return code ? `${getErrorStatus(error) ?? ''}:${code}` : message;
}

/**
 * Global read-QUERY error handler — the read-side companion to
 * handleMutationError. QueryCache.onError fires once per query error (not once
 * per subscribing component), so a shared list backed by one query never
 * double-toasts. On top of that we:
 *  - opt out per query via `meta: { suppressGlobalErrorToast: true }`;
 *  - suppress connectivity failures (the OfflineBanner already says "offline");
 *  - suppress 401 (SessionExpiryHandler re-auths) and page-handled 403/404
 *    (permission-gated / missing-entity views render their own state);
 *  - replace form-validation copy with the standard system-error message (a read
 *    has no form to check);
 *  - dedupe by status + error code within a short window.
 */
export function handleQueryError(
  error: unknown,
  query?: { meta?: Record<string, unknown> },
): void {
  if (query?.meta?.suppressGlobalErrorToast) return;

  if (isNetworkError(error)) return;

  const status = getErrorStatus(error);
  if (status === 401 || status === 403 || status === 404) return;

  const code = getErrorDetail(error)?.code;
  const message =
    code && FORM_VALIDATION_CODES.has(code)
      ? getGenericErrorMessage(error)
      : getErrorMessage(error);

  const key = queryErrorDedupeKey(error, message);
  const now = Date.now();
  const lastAt = recentQueryErrorAt.get(key);
  if (lastAt !== undefined && now - lastAt < DEDUPE_WINDOW_MS) return;
  recentQueryErrorAt.set(key, now);
  // Prune stale keys so the map can't grow unbounded.
  for (const [k, at] of recentQueryErrorAt) {
    if (now - at >= DEDUPE_WINDOW_MS) recentQueryErrorAt.delete(k);
  }

  markErrorSurfaced(error);
  dispatchErrorToast(message);
}
